import { Form } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useTaskManager from '../../context/taskManager/useTaskManager';
import TaskPrompt, { TaskPromptFormFields } from './TaskPrompt';
import { useDebounce } from '@uidotdev/usehooks';
import {
  SearchTaskDefinitionFragment,
  SearchTaskDefinitionsQuery,
  useSearchTaskDefinitionsLazyQuery,
} from '../../graphql/schema';
import Loading from '../base/Loading';
import SearchResultTile from '../taskDefinitions/SearchResultTile';
import TaskDefinitionsPreview from '../taskDefinitions/TaskDefinitionsPreview';

const GenericTask = () => {
  const navigate = useNavigate();
  const { createTaskFromPrompt } = useTaskManager();

  const [term, setTerm] = useState<string>('');
  const debouncedSearchTerm = useDebounce(term, 500);
  const [searchResults, setSearchResults] = useState<
    SearchTaskDefinitionFragment[]
  >([]);

  const [search] = useSearchTaskDefinitionsLazyQuery();

  const [loading, setLoading] = useState<boolean>(false);

  const [promptForm] = Form.useForm<TaskPromptFormFields>();

  const addMessageFromForm = useCallback(
    async (prompt: TaskPromptFormFields) => {
      setLoading(true);
      const task = await createTaskFromPrompt(prompt);
      setLoading(false);

      if (task) navigate(`/task/${task.id}`);
    },
    [navigate, createTaskFromPrompt],
  );

  const handleChangePrompt = useCallback(
    async (prompt: TaskPromptFormFields) => {
      const text = prompt.prompt?.trim() ?? '';
      const numberOfWords = text.split(' ').length;
      if (numberOfWords < 10) setTerm(text);
    },
    [],
  );

  useEffect(() => {
    (async () => {
      if (debouncedSearchTerm === '') return;

      const results = await search({
        variables: {
          options: {
            term: debouncedSearchTerm,
          },
        },
      });
      if (
        results.data?.taskDefinitionsSearch &&
        results.data?.taskDefinitionsSearch.length > 0
      ) {
        setSearchResults(results.data?.taskDefinitionsSearch);
      }
    })();
  }, [debouncedSearchTerm, search]);

  return (
    <div className="flex flex-col flex-1 justify-center items-center">
      <div className="w-full space-y-3">
        <div className="px-2 lg:px-0 space-y-3">
          <div className="w-full text-2xl font-semibold text-center">
            Wie kann ich helfen?
          </div>
          <TaskPrompt
            form={promptForm}
            onSend={addMessageFromForm}
            loading={loading}
            focusOnLoad
            onChange={handleChangePrompt}
            standalone
          />
        </div>

        {searchResults && searchResults.length > 0 && (
          <div className="flex w-full flex-col items-center pt-5 min-h-[277px]">
            <div className="text-xs text-gray-400">
              Passende Vorlagen zu Deiner Anfrage:
            </div>

            <div className="lg:w-[calc(100vw-300px)] w-screen overflow-x-auto">
              <div className="flex flex-row space-x-3 p-2 lg:p-5 pt-3 min-[1640px]:justify-center">
                {searchResults.map((result) => (
                  <SearchResultTile key={result.id} taskDefinition={result} />
                ))}
              </div>
            </div>
          </div>
        )}

        {(!searchResults || searchResults.length === 0) && (
          <div className="flex w-full flex-col items-center pt-5 min-h-[277px]">
            <div className="text-xs text-gray-400">
              ...oder nutze eine Vorlage:
            </div>

            <TaskDefinitionsPreview />
          </div>
        )}
      </div>
    </div>
  );
};

export default GenericTask;
